import { Component, Input, OnInit, OnDestroy, ChangeDetectionStrategy } from "@angular/core";

import { TopicFormService } from "../services/topic-form.service";

import { WebCallTopicRep } from "@onsip/common/services/api/resources/webCallTopic/web-call-topic-rep";
import { Verb } from "@onsip/common/interfaces/verb";
import { BHR, BHRHoliday } from "@onsip/common/interfaces/bhr";
import { countriesList, CountryAvailabilityType } from "@onsip/common/interfaces/countries-list";
import { MatCheckboxChange } from "@angular/material/checkbox";

import { Subscription } from "rxjs";
import { filter } from "rxjs/operators";

export const TARGET_DISPLAY_DEFAULT = "",
  REPS_DEFAULT: Array<WebCallTopicRep> = [],
  VIDEO_DEFAULT = true,
  BHR_DEFAULT: BHR | undefined = undefined,
  BLACKLIST_COUNTRIES_DEFAULT: Array<string> = [],
  WHITELIST_COUNTRIES_DEFAULT: Array<string> = ["US"],
  HOLIDAYS_DEFAULT: Array<BHRHoliday> = [],
  PAGES_DEFAULT: Array<[Verb, string]> = [];

@Component({
  selector: "onsip-topic-write-form",
  templateUrl: "./topic-write-form.component.html",
  styleUrls: ["./topic-write-form.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class TopicWriteFormComponent implements OnInit, OnDestroy {
  @Input() currentTarget: string | undefined;
  @Input() isAdding = false;

  // One for each component
  @Input() targetDisplay = TARGET_DISPLAY_DEFAULT;
  @Input()
  get reps() {
    return this._reps;
  }
  set reps(reps: Array<WebCallTopicRep>) {
    this._reps = reps;
    this.repCount = reps.length;
  }
  @Input() video = VIDEO_DEFAULT;
  @Input() bhr = BHR_DEFAULT;
  @Input() countryAvailabilityStrategy: CountryAvailabilityType = "whitelist";

  countryCount = countriesList.length;
  repCount = 0;
  private _reps = REPS_DEFAULT;

  get countries(): Array<string> {
    return this._countries;
  }
  @Input() set countries(countries: Array<string>) {
    this._countries = countries;
    this.countriesChecked = !countries || (countries && countries.length === 0);
  }

  get holidays(): Array<BHRHoliday> {
    return this._holidays;
  }
  @Input() set holidays(holidays: Array<BHRHoliday>) {
    this._holidays = holidays;
    this.holidaysChecked = !holidays || (holidays && holidays.length === 0);
  }

  get pages(): Array<[Verb, string]> {
    return this._pages;
  }
  @Input() set pages(pages: Array<[Verb, string]>) {
    this._pages = pages;
    this.pagesChecked = !pages || (pages && pages.length === 0);
  }

  get countriesChecked(): boolean {
    return this._countriesChecked;
  }
  set countriesChecked(checked: boolean) {
    this._countriesChecked = checked;
    if (checked) {
      this.countryCount = countriesList.length;
      this._countries = [];
      this.topicForm.removeControl("whitelistCountries");
      this.topicForm.removeControl("blacklistCountries");
    } else if (!checked && (!this._countries || !this._countries.length)) {
      this.countryCount = WHITELIST_COUNTRIES_DEFAULT.length;
      this._countries = WHITELIST_COUNTRIES_DEFAULT;
    }
  }

  get holidaysChecked(): boolean {
    return this._holidaysChecked;
  }
  set holidaysChecked(checked: boolean) {
    this._holidaysChecked = checked;
    checked && this.topicForm.removeControl("holidays");
  }

  get pagesChecked(): boolean {
    return this._pagesChecked;
  }
  set pagesChecked(checked: boolean) {
    this._pagesChecked = checked;
    checked && this.topicForm.removeControl("pages");
  }

  private _countries = WHITELIST_COUNTRIES_DEFAULT;
  private _countriesChecked = true;
  private _holidays = HOLIDAYS_DEFAULT;
  private _holidaysChecked = true;
  private _pages = PAGES_DEFAULT;
  private _pagesChecked = true;
  private unsubscriber = new Subscription();

  constructor(private topicForm: TopicFormService) {}

  onUpdateRepCount(repCount: number): void {
    this.repCount = repCount;
  }

  userCountryCheckChange(checkChange: MatCheckboxChange) {
    if (!checkChange.checked) {
      this.countries = WHITELIST_COUNTRIES_DEFAULT;
      this.countryAvailabilityStrategy = "whitelist";
    }
  }

  ngOnDestroy() {
    this.unsubscriber.unsubscribe();
  }

  ngOnInit() {
    this.countryCount = this.countriesChecked ? countriesList.length : this.countries.length;

    this.unsubscriber.add(
      this.topicForm
        .getForm()
        .valueChanges.pipe(filter(value => value.blacklistCountries || value.whitelistCountries))
        .subscribe(value => {
          if (value.blacklistCountries && value.whitelistCountries) {
            throw new Error("TopicWriteFormComponent: blacklist & whitelist both written to form");
          }
          if (value.blacklistCountries) {
            this.countryCount = countriesList.length - value.blacklistCountries.length;
          } else if (value.whitelistCountries) {
            this.countryCount = value.whitelistCountries.length;
          }
        })
    );
  }
}
